/**
 * <s-email-popup> — Opens the email signup dialog once per visitor.
 *
 * Opens the child <dialog> after `data-delay` seconds (default 10), unless the
 * visitor has already closed it or signed up (remembered in localStorage).
 * Closing the dialog in any way records the dismissal so it never nags again.
 * Skips itself when another dialog is already open. After a signup post from
 * this popup, it re-opens immediately to show the success or error message.
 *
 * Required markup:
 *   <dialog> — Inside self; the signup dialog.
 *
 * Element dataset:
 *   data-delay      — Seconds before opening.
 *   data-submitted  — Present when the page is the response to a customer-form post;
 *                     the dialog re-opens only when the URL hash matches this element's id.
 */
const STORAGE_KEY = 'email-popup-dismissed';

class EmailPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.dialog.addEventListener('close', () => this.remember());

    if (this.hasAttribute('data-submitted')) {
      // Re-open with the result only when the post came from this popup (its return_to hash).
      if (location.hash === `#${this.id}`) {
        this.remember();
        this.open();
      }
      return;
    }
    if (this.dismissed()) return;

    const delay = Number(this.dataset.delay || 10) * 1000;
    this.timer = setTimeout(() => this.open(), delay);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
  }

  open() {
    if (this.dialog.open || document.querySelector('dialog[open]')) return;
    this.dialog.showModal();
  }

  dismissed() {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  remember() {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* storage unavailable: popup may show again next visit */
    }
  }
}

if (!customElements.get('s-email-popup')) customElements.define('s-email-popup', EmailPopup);
